import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { Loading } from "../..";
import {
    ChatStatus, GetTicketStateDocument, GetTicketStatesDocument,
    GetTicketStatesListDocument, useGetTicketStateQuery, UserGroup, useSaveTicketStateMutation,
} from "../../../__generated__/api";
import Confirm from "../../confirm/Confirm";
import ChatStatusPicker from "../../pickers/chat-status/ChatStatus";

export interface DisplayTicketStateProps {
    id?: string;
    isModal?: boolean;
    onSave: (id: Partial<UserGroup>) => void;
    onHide?: () => void;
    className?: string;
}
export default function DisplayTicketState({
    id, onSave, isModal, onHide, className,
}: DisplayTicketStateProps): ReactElement {
    const { data, loading: isLoading } = useGetTicketStateQuery({
        skip: id === "new",
        variables: {
            id,
        },
    });

    const [save] = useSaveTicketStateMutation();

    const [name, setName] = useState(() => data?.ticketState?.name ?? "");
    const [description, setDescription] = useState(() => data?.ticketState?.description ?? "");
    const [chatStatuses, setChatStatuses] = useState<ChatStatus[]>(() => data?.ticketState?.chatStatuses ?? []);

    useEffect(() => {
        if (!data?.ticketState) {
            return;
        }
        setName(data.ticketState.name ?? "");
        setDescription(data.ticketState.description ?? "");
        setChatStatuses(data.ticketState.chatStatuses ?? []);
    }, [data?.ticketState]);

    if (isLoading) {
        return <Loading />;
    }

    const onSubmit = (event?: FormEvent) => {
        event?.preventDefault();
        (async () => {
            const state = {
                name,
                description,
                chatStatuses,
                ticketStatuses: data?.ticketState?.ticketStatuses ?? [],
            };
            const saved = await save({
                refetchQueries: [
                    { query: GetTicketStateDocument, variables: { id } },
                    { query: GetTicketStatesDocument },
                    { query: GetTicketStatesListDocument },
                ],
                variables: {
                    id: id !== "new" ? id : undefined,
                    state,
                },
            });
            onSave({
                ...state,
                id: saved.data?.saveTicketState.id as string,
            });
        })();
    };

    const form = (
        <>

            <div className={className}>
                <h5 className="mb-3 text-light">
                    {!id ? "Create " : "Edit "}
                    {" "}
                    Column
                </h5>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Waiting for customer"
                        required
                        onChange={evt => setName(evt.target.value)}
                        value={name as string}
                    />
                    <Form.Text>
                        The name of the column.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">
                        Description
                        {" "}
                        <small className="text-light">(Optional)</small>
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="This is for tracking all tickets waiting for a user to take action."
                        as="textarea"
                        onChange={evt => setDescription(evt.target.value)}
                        value={description as string}
                    />
                    <Form.Text>
                        Any information to help understand what types of tickets are usually assigned to this column.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">States</Form.Label>
                    <ChatStatusPicker
                        isMulti
                        placeholder="Please pick an ticket state"
                        onChange={statuses => setChatStatuses(statuses)}
                        value={chatStatuses}
                    />
                    <Form.Text>
                        Which ticket statuses does this column manage?
                    </Form.Text>
                </Form.Group>

            </div>

            {!isModal && (
                <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">

                    <button type="submit" className="btn btn-primary">
                        Save
                    </button>
                </div>
            )}
        </>
    );

    return isModal ? (
        <Confirm
            shouldShow
            confirmText="Create"
            onHide={() => onHide?.()}
            onConfirm={() => onSubmit()}
        >
            {form}
        </Confirm>
    ) : (
        <form className="flex-grow-1" onSubmit={onSubmit}>
            {form}
        </form>
    );
}
DisplayTicketState.defaultProps = {
    id: undefined,
    isModal: false,
    onHide: undefined,
    className: undefined,
};
